import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { isDefined } from '@rematter/ui-web';
import { Badge, Button, Menu, Switch } from '@rematter/ui-web';
import { useSetAtom } from 'jotai';
import { LucideTrash2 } from 'lucide-react';

import * as state from '../../_state/customAttributes.state';
import { useTranslations } from '../../i18n/useTranslations';

import { CustomAttributeRecord } from '~/shared/components/custom-attributes/types';

export type CustomAttributeCardProps = {
  customAttribute: CustomAttributeRecord;
};

export const CustomAttributeCard = ({
  customAttribute,
}: CustomAttributeCardProps) => {
  const t = useTranslations();

  const updateCustomAttribute = useSetAtom(state.$updateCustomAttribute);
  const deleteCustomAttribute = useSetAtom(state.$deleteCustomAttribute);
  const setEditingCustomAttributeId = useSetAtom(state.$customAttributeId);

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: customAttribute.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      className="@CustomAttributeCard flex max-w-[1000px] cursor-grab items-center justify-between rounded-lg bg-white p-3 shadow-sm"
      style={style}
      ref={setNodeRef}
      {...listeners}
      {...attributes}
    >
      <div className="flex items-center gap-4">
        <div>
          <Switch
            isSelected={!customAttribute.isDisabled}
            onChange={() => {
              updateCustomAttribute({
                ...customAttribute,
                isDisabled: !customAttribute.isDisabled,
              });
            }}
          />
        </div>
        <div>
          <div className="text-xs text-neutral/80">
            {[customAttribute.dataType, customAttribute.constraints?.objectType]
              .filter(Boolean)
              .join('・')}
          </div>
          <div className="w-[180px] text-sm font-semibold">
            {customAttribute.name}
          </div>
        </div>
        <div className="flex gap-1">
          {isDefined(customAttribute.constraints?.minLength) && (
            <Badge size="xs" className={'opacity-60'}>
              {t.customAttributesView.cards.badges.minLength}・
              {customAttribute.constraints?.minLength}
            </Badge>
          )}
          {isDefined(customAttribute.constraints?.maxLength) && (
            <Badge size="xs" className={'opacity-60'}>
              {t.customAttributesView.cards.badges.maxLength}・
              {customAttribute.constraints?.maxLength}
            </Badge>
          )}
          {isDefined(customAttribute.constraints?.minValue) && (
            <Badge size="xs" className={'opacity-60'}>
              {t.customAttributesView.cards.badges.minValue}・
              {customAttribute.constraints?.minValue}
            </Badge>
          )}
          {isDefined(customAttribute.constraints?.maxValue) && (
            <Badge size="xs" className={'opacity-60'}>
              {t.customAttributesView.cards.badges.maxValue}・
              {customAttribute.constraints?.maxValue}
            </Badge>
          )}

          {!!customAttribute.constraints?.selectOptions?.length && (
            <Badge size="xs">
              {customAttribute.constraints?.selectOptions.join('・')}
            </Badge>
          )}
          {customAttribute.constraints?.unique && (
            <Badge size="xs" color={'primary'}>
              {t.customAttributesView.cards.badges.unique}
            </Badge>
          )}
          {customAttribute.constraints?.multipleValues && (
            <Badge size="xs" color={'success'}>
              {t.customAttributesView.cards.badges.multipleValues}
            </Badge>
          )}
          {customAttribute.constraints?.required && (
            <Badge size="xs" color={'failure'}>
              {t.customAttributesView.cards.badges.required}
            </Badge>
          )}
        </div>
      </div>
      <div className="flex items-center gap-5">
        <div className="flex gap-1">
          <Menu
            trigger={
              <Button variant="outline" color={'failure'}>
                <LucideTrash2 size={16} />
              </Button>
            }
            items={[
              {
                label: t.customAttributesView.cards.actions.confirmDeletion,
                onAction: () =>
                  deleteCustomAttribute({ id: customAttribute.id }),
              },
            ]}
          />
          <Button
            variant="outline"
            color={'primary'}
            onClick={() => setEditingCustomAttributeId(customAttribute.id)}
          >
            {t.customAttributesView.cards.actions.edit}
          </Button>
        </div>
      </div>
    </div>
  );
};
